import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRightLeft, Check, Clock, Mail, RefreshCw, SkipForward, XCircle, Zap,
} from "lucide-react";
import PropTypes from "prop-types";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { signalsApi, outreachApi } from "@/lib/api";
import { applyJourneyToAbmLead } from "@/lib/abmLead";
import { timelineStepState } from "@/lib/rncJourney";
import { canChangeLeadStatus, isQualifiedLeadStatus, assertLeadNotificationConfigured, leadStatusErrorMessage, toastLeadStatusUpdate } from "@/lib/leadStatus";
import { statusColor, chInfo, flattenWorkflow } from "@/pages/signals/rnc/rncData";
import { signalsDashboardPath, SIGNALS_TAB_ABM } from "@/pages/signals/signalsNav";
import {
  RncShell, ChannelBadge, AssignedWorkflowCard,
} from "@/pages/signals/rnc/RncShared";
import { ChangeLeadStatusModal } from "@/pages/signals/rnc/ChangeLeadStatusModal";
import { formatDateTime } from "@/lib/format";
import { WebsiteLink } from "@/components/common/WebsiteLink";
import "@/pages/signals/rnc/rncPlatform.css";

function AbmCrumb({ company }) {
  return (
    <>
      <Link to={signalsDashboardPath()} className="link" style={{ color: "inherit", textDecoration: "none" }}>Signals</Link>
      <span className="sep">/</span>
      <Link to={signalsDashboardPath(SIGNALS_TAB_ABM)} className="link" style={{ color: "inherit", textDecoration: "none" }}>ABM List</Link>
      <span className="sep">/</span>
      <b>{company}</b>
    </>
  );
}

AbmCrumb.propTypes = {
  company: PropTypes.string.isRequired,
};

function AbmLeadCard({ lead }) {
  return (
    <>
      <div className="lead-hero">
        <div className="mk" style={{ background: lead.color }}>{lead.initials}</div>
        <div>
          <div className="nm">{lead.company}</div>
          <div className="sub">{lead.type} · {lead.units}</div>
        </div>
      </div>
      <div className="field idfield">
        <label htmlFor={`abm-id-${lead.id}`}>LEAD ID</label>
        <div id={`abm-id-${lead.id}`} className="val">{lead.id}</div>
      </div>
      <div className="field">
        <label htmlFor={`abm-contact-${lead.id}`}>CONTACT PERSON</label>
        <div id={`abm-contact-${lead.id}`} className="val">{lead.contact}</div>
      </div>
      <div className="field">
        <label htmlFor={`abm-email-${lead.id}`}>EMAIL</label>
        <div id={`abm-email-${lead.id}`} className="val link">{lead.email}</div>
      </div>
      <div className="field">
        <label htmlFor={`abm-phone-${lead.id}`}>PHONE NUMBER</label>
        <div id={`abm-phone-${lead.id}`} className="val">{lead.phone}</div>
      </div>
      <div className="field">
        <label htmlFor={`abm-location-${lead.id}`}>LOCATION</label>
        <div id={`abm-location-${lead.id}`} className="val">
          {[lead.city, lead.state, lead.country].filter(Boolean).join(", ") || "—"}
        </div>
      </div>
      <div className="field">
        <label htmlFor={`abm-website-${lead.id}`}>WEBSITE</label>
        <WebsiteLink
          id={`abm-website-${lead.id}`}
          className="val link"
          value={lead.website}
          data-testid="abm-lead-website"
        />
      </div>
      <div className="field">
        <label htmlFor={`abm-channel-${lead.id}`}>SOURCE</label>
        <div id={`abm-channel-${lead.id}`} className="val" style={{ display: "flex", alignItems: "center", gap: 7 }} data-testid="abm-lead-source">
          <ChannelBadge channelKey={lead.channel} size={18} /> {chInfo(lead.channel).name}
        </div>
      </div>
      <div className="field">
        <label htmlFor={`abm-date-${lead.id}`}>DATE ADDED</label>
        <div id={`abm-date-${lead.id}`} className="val">{formatDateTime(lead.dateAdded)}</div>
      </div>
    </>
  );
}

AbmLeadCard.propTypes = {
  lead: PropTypes.object.isRequired,
};

/** Expandable step description — truncated with ellipsis at 2 lines, expandable on click. */
function StepDesc({ detail, branchLabel }) {
  const [expanded, setExpanded] = useState(false);
  const text = detail || "";
  if (!text && !branchLabel) return null;
  return (
    <>
      {branchLabel && (
        <div style={{ marginTop: 2 }}>
          <span className="tag purple">{branchLabel}</span>
        </div>
      )}
      <div
        className={`desc${expanded ? " expanded" : ""}`}
        title={text}
      >
        {text}
      </div>
      {text.length > 80 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{
            display: "inline-block",
            background: "none",
            border: "none",
            padding: 0,
            marginTop: 2,
            color: "var(--indigo)",
            fontSize: "12px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </>
  );
}
StepDesc.propTypes = { detail: PropTypes.string, branchLabel: PropTypes.string };

function WorkflowTimeline({ workflow, leadStatus, stepIndex, exited, stepFailures, stepSkips, waiting = false }) {
  const flat = flattenWorkflow(workflow);
  if (!flat.length) {
    return (
      <p style={{ color: "var(--slate-400)", fontSize: 13, margin: 0 }}>
        No workflow assigned yet for this ABM lead.
      </p>
    );
  }
  return (
    <div className="timeline">
      {exited && (
        <p
          style={{ color: "var(--slate-500)", fontSize: 13, margin: "0 0 14px" }}
          data-testid="abm-timeline-exited-note"
        >
          This lead exited the campaign — remaining steps were cancelled.
        </p>
      )}
      {flat.map((s, i) => {
        const { cls, when: whenText, stopped, error } = timelineStepState({
          index: i,
          stepIndex,
          status: leadStatus,
          exited,
          stepId: s.id,
          stepFailures,
          stepSkips,
          stepBadge: s.badge,
          waiting,
        });

        let IconComp = <Mail size={12} />;
        if (cls === "done") IconComp = <Check size={12} strokeWidth={3} />;
        else if (cls === "active") IconComp = <Clock size={12} />;
        else if (cls === "skipped") IconComp = <SkipForward size={12} />;
        else if (cls === "failed" || stopped) IconComp = <XCircle size={12} />;

        return (
          <div className={`tstep ${cls}`} key={`${s.id || s.title}-${i}`}>
            <div className={`node ${cls}`}>{IconComp}</div>
            <div className="body">
              <div className="row1">
                <span className="ttl">{s.title}</span>
                <span className="when">{whenText}</span>
              </div>
              <StepDesc detail={s.detail} branchLabel={s.branchLabel} />
              {error && cls === "failed" ? <div className="step-error">{error}</div> : null}
              {error && cls === "skipped" ? <div className="step-skip">{error}</div> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

WorkflowTimeline.propTypes = {
  workflow: PropTypes.object,
  leadStatus: PropTypes.string,
  stepIndex: PropTypes.number,
  exited: PropTypes.bool,
  stepFailures: PropTypes.object,
  stepSkips: PropTypes.object,
  waiting: PropTypes.bool,
};

export default function AbmDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, lead: null, journey: null, error: null });
  const [reloadToken, setReloadToken] = useState(0);
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const leadId = decodeURIComponent(id || "").trim();
    setState({ loading: true, lead: null, journey: null, error: null });
    Promise.all([
      signalsApi.getCustomLead(leadId),
      outreachApi.getAbmLeadJourney(leadId).catch(() => null),
    ])
      .then(([data, journey]) => {
        if (!cancelled) {
          setState({
            loading: false,
            lead: data?.lead || null,
            journey,
            error: null,
          });
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setState({
            loading: false,
            lead: null,
            journey: null,
            error: e.message || "Failed to load lead",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadToken]);

  const lead = useMemo(() => {
    if (!state.lead) return null;
    return applyJourneyToAbmLead(state.lead, state.journey?.journey || state.journey);
  }, [state.lead, state.journey]);

  const progressPct = lead?.progress == null ? null : Math.round(Number(lead.progress) * 100);
  const statusBadge = lead?.status || "Pending";
  const statusCls = lead?.status ? statusColor(lead.status) : "gray";
  const showStatusBtn = lead ? canChangeLeadStatus(lead) : false;

  const handleStatusSubmit = async ({ lead_status, comment }) => {
    setStatusSaving(true);
    try {
      if (isQualifiedLeadStatus(lead_status)) {
        await assertLeadNotificationConfigured();
      }
      const result = await outreachApi.setEnrollmentLeadStatus(
        lead.campaignId,
        lead.enrollmentId,
        lead_status,
        comment,
      );
      toastLeadStatusUpdate(toast, result, lead_status);
      setStatusOpen(false);
      setReloadToken((n) => n + 1);
    } catch (e) {
      toast.error(leadStatusErrorMessage(e));
    } finally {
      setStatusSaving(false);
    }
  };

  if (state.loading) {
    return (
      <RncShell crumb={<AbmCrumb company="Loading…" />}>
        <div className="card" style={{ padding: 20 }} data-testid="abm-detail-loading">
          <Skeleton className="h-8 w-1/2 mb-4" />
          <Skeleton className="h-40 w-full mb-3" />
          <Skeleton className="h-56 w-full" />
        </div>
      </RncShell>
    );
  }

  if (state.error || !lead) {
    return (
      <RncShell crumb={<AbmCrumb company="Lead" />}>
        <div className="card" style={{ padding: 20 }} data-testid="abm-detail-error">
          <p style={{ color: "#b91c1c", margin: 0 }}>{state.error || "Lead not found"}</p>
          <p style={{ marginTop: 12 }}>
            <Link to={signalsDashboardPath(SIGNALS_TAB_ABM)} style={{ color: "var(--indigo)" }}>Return to ABM List</Link>
          </p>
        </div>
      </RncShell>
    );
  }

  return (
    <RncShell crumb={<AbmCrumb company={lead.company} />}>
      <div className="page-head" data-testid="abm-lead-detail-page">
        <div>
          <h1>{lead.company}</h1>
          <p>
            {[lead.city, lead.country].filter(Boolean).join(", ") || "ABM List lead"}
            {" · "}
            sourced from ABM List.
          </p>
          <div className="leadid-tag">Lead ID · {lead.id}</div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0, flexWrap: "wrap" }}>
          <button
            type="button"
            className="btn"
            onClick={() => navigate(signalsDashboardPath(SIGNALS_TAB_ABM))}
            data-testid="abm-detail-back"
          >
            <ArrowLeft size={15} /> Back
          </button>
          <span className={`badge ${statusCls}`}>
            <span className="dotb" />
            {statusBadge}
            {progressPct != null ? ` · ${progressPct}%` : ""}
          </span>
          {showStatusBtn ? (
            <button
              type="button"
              className="btn primary"
              onClick={() => setStatusOpen(true)}
              data-testid="abm-change-status"
            >
              <ArrowRightLeft size={15} /> Change status
            </button>
          ) : null}
          <button
            type="button"
            className="btn"
            onClick={() => setReloadToken((n) => n + 1)}
            data-testid="abm-detail-refresh"
          >
            <RefreshCw size={15} /> Refresh
          </button>
        </div>
      </div>

      <ChangeLeadStatusModal
        open={statusOpen}
        onOpenChange={setStatusOpen}
        company={lead.company}
        rncId={lead.id}
        currentLeadStatus={lead.leadStatus}
        saving={statusSaving}
        onSubmit={handleStatusSubmit}
      />

      <div className="grid2">
        <div className="left-col card">
          <AbmLeadCard lead={lead} />
          <div className="field">
            <label htmlFor={`abm-notes-${lead.id}`}>NOTES</label>
            <div id={`abm-notes-${lead.id}`} className="notes-box">
              {lead.notes || "No notes on this lead."}
            </div>
          </div>
          {lead.statusComment ? (
            <div className="field" style={{ marginTop: 12 }}>
              <label htmlFor={`abm-status-comment-${lead.id}`}>STATUS COMMENT</label>
              <div id={`abm-status-comment-${lead.id}`} className="notes-box" data-testid="abm-status-comment">
                {lead.statusComment}
              </div>
            </div>
          ) : null}
          <div className="left-col-footer">
            <RefreshCw size={12} /> Imported {formatDateTime(lead.lastActivity)} · Record {lead.id}
          </div>
        </div>

        <div>
          <AssignedWorkflowCard
            rnc={lead}
            onViewWorkflow={() => {
              navigate(`/signals/abm/${encodeURIComponent(lead.id)}/workflow`);
            }}
          />
          <div className="card">
            <h3><Zap size={16} /> AI Outreach Progress</h3>
            <WorkflowTimeline
              workflow={lead.signalWorkflow}
              leadStatus={lead.status}
              stepIndex={lead.stepIndex}
              exited={Boolean(lead.exited)}
              stepFailures={lead.stepFailures || {}}
              stepSkips={lead.stepSkips || {}}
              waiting={Boolean(lead.workflowWaiting)}
            />
          </div>
        </div>
      </div>
    </RncShell>
  );
}
